import { useEffect, useRef, useState } from 'react';
import AutocompleteInput, {
  AutocompleteInputProps,
  Describable,
} from '@peachjar/ui/dist/lib/components/Inputs/AutocompleteInput';
import { useAriaInvalid } from '@/hooks';
import styles from './Autocomplete.styles';

type Props<T extends Describable> = AutocompleteInputProps<T> & {
  id: string;
  required?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
};
export const Autocomplete = <T extends Describable>({
  id,
  label,
  lookup,
  renderOption,
  onChange,
  value,
  error,
  required,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  ...props
}: Props<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const getInput = () => comboboxRef.current?.querySelector<HTMLInputElement>('input') ?? null;

  useEffect(() => {
    const input = getInput();
    const container = comboboxRef.current;
    if (!input || !container) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        event.preventDefault();
      }
    };

    // MUI's useAutocomplete hook applies aria-expanded to its root wrapper <div>
    // (via getRootProps()), not directly on the <input>. We observe the full subtree
    // so we catch that change. Mutations on the input itself are skipped to avoid
    // echoing back the value we write in the second effect.
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.target === input) continue;
        const expanded = (mutation.target as Element).getAttribute('aria-expanded') === 'true';
        setIsDropdownOpen(prev => (prev === expanded ? prev : expanded));
      }
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    });
    input.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      input.removeEventListener('keydown', handleKeyDown);
    };
  }, [id]);

  useEffect(() => {
    const input = getInput();
    if (!input) return;
    const descriptionId = `${id}-description`;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', isDropdownOpen.toString());
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-autocomplete', 'list');
    if (required) {
      input.setAttribute('required', 'true');
      input.setAttribute('aria-required', 'true');
    } else {
      input.removeAttribute('required');
      input.removeAttribute('aria-required');
    }
    const helperText = comboboxRef.current?.querySelector('.MuiFormHelperText-root');
    if (helperText instanceof HTMLElement) {
      helperText.id = descriptionId;
    }
    const describedByIds = [error ? descriptionId : null, ariaDescribedBy ?? null]
      .filter(Boolean)
      .join(' ');
    if (describedByIds) {
      input.setAttribute('aria-describedby', describedByIds);
    } else {
      input.removeAttribute('aria-describedby');
    }
    const effectiveAriaLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined);
    if (effectiveAriaLabel) {
      input.setAttribute('aria-label', effectiveAriaLabel);
    }
  }, [isDropdownOpen, error, id, label, ariaLabel, required, ariaDescribedBy]);

  useAriaInvalid(getInput, error, [id]);

  const rootClass = required ? `${styles.root} ${styles.required}` : styles.root;

  return (
    <div ref={comboboxRef} className={rootClass}>
      <AutocompleteInput<T>
        {...props}
        value={value}
        label={label}
        error={error}
        lookup={lookup}
        renderOption={(choice, state) => {
          return (
            <div
              id={`${id}-option-${choice.description}`}
              role="option"
              aria-selected={state.selected}
            >
              {renderOption(choice, state)}
            </div>
          );
        }}
        onChange={val => {
          onChange?.(val);
          setIsDropdownOpen(false);
        }}
        data-testid={id}
      />
    </div>
  );
};
